import { config } from '../config';
import { tr } from '../i18n/tr';
import { readApiError } from '../lib/apiError';
import type { Look } from '../lib/appearance';

type ApiFetch = (url: string, init?: RequestInit) => Promise<Response>;

/** messaging-gateway `AppearanceView`: the effective look and where it came from. */
export interface AppearanceView {
  look: Look;
  source: 'tenant' | 'system' | 'default';
  /** The system default (global admin), null when unset. */
  system: Look | null;
  /** The workspace's own look, null when it follows the system default. */
  tenant: Look | null;
}

const base = () => `${config.apiServer}/api/appearance`;

export const appearanceService = {
  /** Effective look for a workspace (or the system default when no workspace is open). */
  async get(apiFetch: ApiFetch, tenantId?: string | null): Promise<AppearanceView> {
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const res = await apiFetch(`${base()}${qs}`, { method: 'GET' });
    if (!res.ok) throw await readApiError(res, tr('apiAction.loadLook'));
    return (await res.json()) as AppearanceView;
  },

  /** Global admin: the look everyone gets unless their workspace picked one. */
  async setSystem(apiFetch: ApiFetch, look: Look): Promise<AppearanceView> {
    const res = await apiFetch(`${base()}/system`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ look }),
    });
    if (!res.ok) throw await readApiError(res, tr('apiAction.saveSystemLook'));
    return (await res.json()) as AppearanceView;
  },

  /** Workspace owner/admin: this workspace's look; null = follow the system default. */
  async setTenant(apiFetch: ApiFetch, tenantId: string, look: Look | null): Promise<AppearanceView> {
    const res = await apiFetch(`${base()}/tenants/${encodeURIComponent(tenantId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ look }),
    });
    if (!res.ok) throw await readApiError(res, tr('apiAction.saveWorkspaceLook'));
    return (await res.json()) as AppearanceView;
  },
};
